import { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate, fmtDateTime, label } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { Card, Empty, Field, PageHeader, Td, Th } from "../../components/common/UI";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const KIND = { measure: "bg-sky-100 text-sky-800", text: "bg-emerald-100 text-emerald-800", document: "bg-amber-100 text-amber-800" };

function Highlight({ text, q }) {
  const terms = (q.toLowerCase().match(/[\w-]{2,}/g) || []).map((t) => t.replace(/[-]/g, "\\-"));
  if (!text || !terms.length) return text || null;
  const parts = text.split(new RegExp(`(${terms.join("|")})`, "gi"));
  return parts.map((p, i) => (terms.some((t) => p.toLowerCase() === t.replace(/\\/g, "")) ? <mark key={i} className="bg-yellow-200">{p}</mark> : p));
}

function SearchTab() {
  const { can } = useAuth();
  const [f, setF] = useState({ q: "", scope: "all", archived_only: false });
  const [res, setRes] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadStatus = useCallback(() => api.get("/search/index-status").then((r) => setStatus(r.data)), []);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  const run = (e) => {
    e?.preventDefault();
    setBusy(true);
    api.get("/search", { params: { ...f, archived_only: f.archived_only || undefined } }).then((r) => setRes(r.data)).catch((x) => toast.error(errMsg(x))).finally(() => setBusy(false));
  };
  const reindex = () => api.post("/search/reindex").then((r) => { toast.success(`Indexed ${r.data.indexed} document(s)`); loadStatus(); }).catch((x) => toast.error(errMsg(x)));
  return (
    <div className="space-y-4">
      <form onSubmit={run} className="flex flex-col gap-2 rounded-md border border-slate-200 bg-white p-3 shadow-sm md:flex-row md:items-end" data-testid="search-form">
        <Field label="Search titles, measure text and uploaded documents" className="flex-1">
          <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <input className="otms-input" style={{ paddingLeft: "2rem" }} value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="e.g. disaster risk reduction, plastic, Ordinance No. 2026-015" data-testid="search-input" /></div>
        </Field>
        <Field label="Search in">
          <select className="otms-input" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} data-testid="search-scope">
            <option value="all">Everything</option><option value="measures">Titles and abstracts</option><option value="texts">Measure text</option><option value="documents">Uploaded documents</option>
          </select>
        </Field>
        <label className="flex items-center gap-2 pb-2 text-xs"><input type="checkbox" checked={f.archived_only} onChange={(e) => setF({ ...f, archived_only: e.target.checked })} data-testid="search-archived-only" />Archived only</label>
        <Button type="submit" disabled={busy || f.q.trim().length < 2} className="bg-sky-600 hover:bg-sky-700" data-testid="search-submit">Search</Button>
      </form>
      {status && (
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500" data-testid="index-status">
          <span>{status.indexed}/{status.documents} documents searchable</span>
          <span className="text-amber-700">{status.ocr_pending} scanned, awaiting OCR (stub)</span>{status.failed > 0 && <span className="text-red-700">{status.failed} failed</span>}
          {can("measure.archive") && status.not_indexed > 0 && <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={reindex} data-testid="reindex-button">Index {status.not_indexed} remaining</Button>}
        </div>
      )}
      {res && (
        <div className="space-y-3" data-testid="search-results">
          <div className="text-xs text-slate-500" data-testid="search-total">{res.total} measure(s) match “{res.query}”</div>
          {res.results.length === 0 && <Empty text="No matches. Try fewer or different words." />}
          {res.results.map((r) => (
            <div key={r.measure_id} className="rounded-md border border-slate-200 bg-white p-3 shadow-sm" data-testid={`search-hit-${r.measure_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Link to={`/measures/${r.measure_id}`} className="font-semibold text-sky-700 hover:underline">{r.measure_ref}</Link>
                <span className="text-[11px] text-slate-500">{r.measure_type} · {label(r.measure_status)}</span>
                {r.archived && r.measure_status !== "archived" && <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-white">Archived</span>}
              </div>
              <div className="text-sm text-slate-800">{r.measure_title}</div>
              <div className="mt-2 space-y-1">
                {r.matches.map((mt, i) => (
                  <div key={i} className="flex gap-2 text-xs">
                    <span className={`h-fit shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${KIND[mt.kind]}`}>{mt.label}</span>
                    <span className="text-slate-600"><Highlight text={mt.snippet} q={res.query} /></span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ArchiveEdit({ a, onDone }) {
  const [f, setF] = useState(a);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = () => api.put(`/measures/${a.measure_id}/archive`, f).then(() => { toast.success("Archive details saved"); onDone(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <tr><td colSpan={6} className="bg-slate-50 p-3">
      <div className="grid gap-2 md:grid-cols-4" data-testid="archive-edit-form">
        <Field label="Records series"><input className="otms-input" value={f.records_series || ""} onChange={up("records_series")} data-testid="archive-series" /></Field>
        <Field label="Physical location"><input className="otms-input" value={f.physical_location || ""} onChange={up("physical_location")} data-testid="archive-location" /></Field>
        <Field label="Box no."><input className="otms-input" value={f.box_no || ""} onChange={up("box_no")} data-testid="archive-box" /></Field>
        <Field label="Folder no."><input className="otms-input" value={f.folder_no || ""} onChange={up("folder_no")} data-testid="archive-folder" /></Field>
        <Field label="Media"><select className="otms-input" value={f.media} onChange={up("media")} data-testid="archive-media"><option value="paper">Paper</option><option value="digital">Digital</option><option value="both">Paper and digital</option></select></Field>
        <label className="flex items-center gap-2 pt-6 text-xs"><input type="checkbox" checked={!!f.digitized} onChange={up("digitized")} data-testid="archive-digitized" />Paper original digitized</label>
        <Field label="Remarks" className="md:col-span-2"><input className="otms-input" value={f.remarks || ""} onChange={up("remarks")} data-testid="archive-remarks" /></Field>
        <div className="flex gap-2 md:col-span-4"><Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid="archive-save">Save</Button><Button size="sm" variant="ghost" onClick={onDone}>Cancel</Button></div>
      </div>
    </td></tr>
  );
}

function ArchiveTab() {
  const { can } = useAuth();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/archive", { params: { q: q || undefined } }).then((r) => setRows(r.data)), [q]);
  useEffect(() => { load(); }, [load]);
  return (
    <Card testId="archive-register">
      <input className="otms-input mb-3 max-w-sm" placeholder="Filter by archive ref, number, title, location…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="archive-filter" />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px]" data-testid="archive-table">
          <thead><tr><Th>Archive ref</Th><Th>Measure</Th><Th>Archived</Th><Th>Location</Th><Th>Media</Th><Th /></tr></thead>
          <tbody>
            {(rows || []).map((a) => (<Fragment key={a.id}>
              <tr>
                <Td className="font-mono text-xs">{a.archive_ref}</Td>
                <Td><Link className="font-medium text-sky-700 hover:underline" to={`/measures/${a.measure_id}`}>{a.measure_ref}</Link><div className="text-[11px] text-slate-500">{a.measure_title}</div></Td>
                <Td>{fmtDate(a.archived_at)}</Td>
                <Td>{[a.physical_location, a.box_no && `Box ${a.box_no}`, a.folder_no && `Folder ${a.folder_no}`].filter(Boolean).join(" · ") || "—"}</Td>
                <Td>{label(a.media)}{a.digitized && " · digitized"}</Td>
                <Td>{can("measure.archive") && <Button size="sm" variant="ghost" onClick={() => setEdit(a.id)} data-testid={`archive-edit-${a.id}`}>Edit</Button>}</Td>
              </tr>
              {edit === a.id && <ArchiveEdit a={a} onDone={() => { setEdit(null); load(); }} />}
            </Fragment>))}
          </tbody>
        </table>
        {rows && rows.length === 0 && <Empty text="No archived measures yet. Measures are archived through the workflow after effectivity is verified." />}
      </div>
    </Card>
  );
}

function CopiesTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/certified-copies").then((r) => setRows(r.data)); }, []);
  return (
    <Card testId="ctc-register">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px]" data-testid="ctc-table">
          <thead><tr><Th>Control no.</Th><Th>Measure</Th><Th>Requested by</Th><Th>Purpose</Th><Th>Issued</Th><Th /></tr></thead>
          <tbody>
            {(rows || []).map((c) => (
              <tr key={c.id}>
                <Td className="font-mono text-xs">{c.control_no}</Td>
                <Td><Link className="font-medium text-sky-700 hover:underline" to={`/measures/${c.measure_id}`}>{c.measure_ref}</Link></Td>
                <Td>{c.requested_by}{c.or_number && <div className="text-[11px] text-slate-500">O.R. {c.or_number}</div>}</Td><Td>{c.purpose}</Td>
                <Td>{fmtDateTime(c.issued_at)}<div className="text-[11px] text-slate-500">{c.issued_by_name}</div></Td>
                <Td><a className="text-xs text-sky-700 hover:underline" href={`/measures/${c.measure_id}/certified-copy?issue=${c.id}`} target="_blank" rel="noreferrer" data-testid={`ctc-reprint-${c.id}`}>Reprint</a></Td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows && rows.length === 0 && <Empty text="No certified true copies issued yet." />}
      </div>
    </Card>
  );
}

export default function ArchivePage() {
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Records" title="Documents and Archive" subtitle="Full-text search across measures and uploaded documents, the archive register, and the register of certified true copies." />
      <Tabs defaultValue="search" className="min-w-0">
        <TabsList className="mb-4 flex h-auto max-w-full flex-wrap justify-start bg-slate-100">
          <TabsTrigger value="search" data-testid="archive-tab-search">Full-text search</TabsTrigger>
          <TabsTrigger value="archive" data-testid="archive-tab-register">Archive register</TabsTrigger>
          <TabsTrigger value="copies" data-testid="archive-tab-copies">Certified copies</TabsTrigger>
        </TabsList>
        <TabsContent value="search"><SearchTab /></TabsContent>
        <TabsContent value="archive"><ArchiveTab /></TabsContent>
        <TabsContent value="copies"><CopiesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
